import { useEffect, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { AlertTriangle, ChevronLeft, ChevronRight, Search, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import type { IncidentFilters, IncidentSeverity, IncidentStatus, IncidentSummary, Pagination } from './types'

interface Props {
  incidents: IncidentSummary[]
  pagination: Pagination
  filters: IncidentFilters
  statusOptions: IncidentStatus[]
  severityOptions: IncidentSeverity[]
  sourceOptions: string[]
  organizationOptions: { slug: string; name: string }[]
}

const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Crítico',
  high: 'Alto',
  medium: 'Médio',
  ignored: 'Ignorado',
}

const STATUS_LABELS: Record<string, string> = {
  open: 'Aberto',
  acknowledged: 'Reconhecido',
  resolved: 'Resolvido',
  suppressed: 'Suprimido',
}

const SEVERITY_CLASSES: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400',
  medium: 'bg-amber-100 text-amber-700 dark:bg-yellow-500/15 dark:text-yellow-400',
  ignored: 'bg-accent text-muted-foreground',
}

function Pill({ label, className }: { label: string; className: string }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>{label}</span>
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function IncidentsIndex({ incidents, pagination, filters, statusOptions, severityOptions, sourceOptions, organizationOptions }: Props) {
  const [query, setQuery] = useState(filters.search)

  useEffect(() => setQuery(filters.search), [filters.search])

  const navigate = (next: Partial<IncidentFilters> & { page?: number }) => {
    router.get('/cc/incidents', { ...filters, ...next }, { preserveState: true, preserveScroll: true })
  }

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault()
    navigate({ search: query, page: 1 })
  }

  const resetFilters = () => {
    router.get('/cc/incidents', {
      search: '',
      severity: '',
      status: '',
      organization_slug: '',
      instance_name: '',
      source: '',
      date_from: '',
      date_to: '',
      page: 1,
    })
  }

  const hasFilters = Object.values(filters).some((value) => value)

  return (
    <div>
      <div className="flex flex-col gap-3 mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-foreground" />
            <h2 className="text-2xl font-bold text-foreground">Incidentes</h2>
            <span className="text-sm text-muted-foreground">({pagination.total})</span>
          </div>
          {hasFilters && (
            <Button type="button" variant="outline" size="sm" onClick={resetFilters}>
              <XCircle className="h-4 w-4 mr-1" /> Limpar
            </Button>
          )}
        </div>

        <form onSubmit={submitSearch} className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar título, impacto ou fingerprint..." className="pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring w-72" />
          </div>
          <select value={filters.severity} onChange={(e) => navigate({ severity: e.target.value, page: 1 })} className="py-2 px-3 text-sm border border-border rounded-lg bg-background">
            <option value="">Todas severidades</option>
            {severityOptions.map((severity) => <option key={severity} value={severity}>{SEVERITY_LABELS[severity]}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => navigate({ status: e.target.value, page: 1 })} className="py-2 px-3 text-sm border border-border rounded-lg bg-background">
            <option value="">Todos status</option>
            {statusOptions.map((status) => <option key={status} value={status}>{STATUS_LABELS[status]}</option>)}
          </select>
          <select value={filters.organization_slug} onChange={(e) => navigate({ organization_slug: e.target.value, page: 1 })} className="py-2 px-3 text-sm border border-border rounded-lg bg-background">
            <option value="">Todas orgs</option>
            {organizationOptions.map((org) => <option key={org.slug} value={org.slug}>{org.name}</option>)}
          </select>
          <select value={filters.source} onChange={(e) => navigate({ source: e.target.value, page: 1 })} className="py-2 px-3 text-sm border border-border rounded-lg bg-background">
            <option value="">Todas fontes</option>
            {sourceOptions.map((source) => <option key={source} value={source}>{source}</option>)}
          </select>
          <Button type="submit" variant="outline" size="sm">Buscar</Button>
        </form>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-260px)]">
          <table className="w-full">
            <thead className="sticky top-0 z-10 bg-muted border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Incidente</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Severidade</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Instância</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Fontes</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Última ocorrência</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {incidents.map((incident) => (
                <tr key={incident.id} className="hover:bg-accent transition-colors">
                  <td className="px-4 py-3 min-w-[260px]">
                    <Link href={incident.workstationUrl} className="text-sm font-medium text-foreground hover:text-blue-600">{incident.title}</Link>
                    <div className="text-xs text-muted-foreground mt-1">{incident.fingerprint}</div>
                    {incident.slackDryRunRef && <div className="text-xs text-muted-foreground mt-1">{incident.slackDryRunRef}</div>}
                  </td>
                  <td className="px-4 py-3"><Pill label={SEVERITY_LABELS[incident.severity]} className={SEVERITY_CLASSES[incident.severity]} /></td>
                  <td className="px-4 py-3 text-sm text-foreground">{STATUS_LABELS[incident.status]}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{incident.affectedInstances.join(', ') || '—'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{incident.correlatedSources.join(', ') || incident.primarySource}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(incident.lastSeenAt)}</td>
                </tr>
              ))}
              {incidents.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhum incidente encontrado.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-3 border-t border-border">
          <span className="text-sm text-muted-foreground">Página {pagination.page} de {pagination.totalPages}</span>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => navigate({ page: pagination.page - 1 })}><ChevronLeft className="h-4 w-4" /> Anterior</Button>
            <Button type="button" variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => navigate({ page: pagination.page + 1 })}>Próxima <ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
    </div>
  )
}

IncidentsIndex.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>

export default IncidentsIndex
